/**
 * درِ ورودیِ «سیگنالِ جدید» — قرینه‌ی وبِ `features/signals/useNewSignal` در اپ.
 *
 * تنها کارش یه سوالِ یه‌باره‌ست: اگه کاربر هیچ‌کس رو توی فهرستِ دوستانش
 * نداره، قبل از باز شدنِ فرم می‌پرسیم ادامه بده یا بره صفحه‌ی دوستان. منطقش
 * (و اینکه چرا فقط یه‌بار برای هر کاربر) توی `core/lib/noFriendsNotice`
 * توضیح داده شده.
 *
 * چرا Provider و نه یه هوکِ ساده: پنجره باید **یه جا** رندر شه، نه هر جایی
 * که یه دکمه‌ی «سیگنالِ جدید» هست — هدر، منوی گوشی و صفحه‌ی رخداد هر سه
 * همین یه پنجره رو باز می‌کنن. پس پنجره این‌جا می‌شینه و بقیه فقط
 * `useNewSignal()` رو صدا می‌زنن.
 *
 * ورودیِ اون تابع خودِ کاری‌ست که قرار بود بکنه (`go`)، نه یه آدرس: صداکننده
 * ممکنه قبل از رفتن فرم رو پیش‌پُر کنه (صفحه‌ی رخداد)، و این‌طوری هیچ‌کدوم
 * عوض نمی‌شن، فقط عقب می‌افتن تا جوابِ کاربر بیاد.
 */
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@/core/lib/i18n';
import { useSession } from '@/core/session';
import { needsNoFriendsNotice } from '@/core/lib/noFriendsNotice';

type StartNewSignal = (go: () => void) => void;

/** پیش‌فرضِ بیرونِ Provider: هیچ سوالی، فقط برو — تا هیچ دکمه‌ای از کار نیفته. */
const Ctx = createContext<StartNewSignal>((go) => go());

export const useNewSignal = (): StartNewSignal => useContext(Ctx);

export function NewSignalGuard({ children }: { children: React.ReactNode }) {
  const { session } = useSession();
  const nav = useNavigate();
  /** کارِ معلق تا وقتی پنجره بازه؛ `null` یعنی پنجره‌ای نیست. */
  const [pending, setPending] = useState<{ go: () => void } | null>(null);

  const start = useCallback<StartNewSignal>((go) => {
    /**
     * شناسه از **نشست** گرفته می‌شه نه از پروفایل: درست بعدِ ورود، نشست
     * هست ولی ردیفِ پروفایل هنوز داره می‌آد — و همون چند صدم ثانیه یعنی
     * اولین کلیکِ کاربر بی‌سوال رد می‌شد. (تستِ E2E دقیقاً همین رو گرفت.)
     */
    const uid = session?.user?.id;
    if (!uid) { go(); return; }

    needsNoFriendsNotice(uid)
      .then((needed) => (needed ? setPending({ go }) : go()))
      // هر شکستی این‌جا یعنی فقط سوال نپرسیدن، نه ایستادنِ کاربر.
      .catch(() => go());
  }, [session?.user?.id]);

  const cont = () => {
    const { go } = pending!;
    setPending(null);
    go();
  };

  const toFriends = () => {
    setPending(null);
    nav('/friends');
  };

  return (
    <Ctx.Provider value={start}>
      {children}

      {pending ? (
        // همون قابِ همیشگیِ پنجره‌ها (نگاه کن به .modal در ui/base.css).
        // کلیکِ بیرون یعنی «هیچ‌کدوم» — سوال پرسیده شده و دیگه تکرار نمی‌شه.
        <div className="modal-backdrop" role="dialog" aria-modal="true"
          data-testid="no-friends-dialog"
          onClick={() => setPending(null)}>
          <div className="card modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('noFriendsAlertTitle')}</h3>
            <p className="dim">{t('noFriendsAlertBody')}</p>
            {/* «ادامه» دکمه‌ی اصلیه: این یه هشداره، نه یه سد. */}
            <button type="button" className="btn fill block"
              data-testid="no-friends-continue" onClick={cont}>
              {t('noFriendsAlertContinue')}
            </button>
            <button type="button" className="btn ghost block"
              data-testid="no-friends-friends" onClick={toFriends}>
              {t('noFriendsAlertFriends')}
            </button>
          </div>
        </div>
      ) : null}
    </Ctx.Provider>
  );
}
